import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { generateDefaultAccessPermission } from '@utils/permissions';

import { IBrandingSetup } from '@interfaces/platform/branding';
import { TDashboardAvatar } from '@interfaces/platform/manageAssetTypes';
import { IUserKeycloak } from '@interfaces/user/user';

import { EUserRoles, TKeyOfUserRoles, TOrganizationType, TUserRoles } from '@enums/common';

import { STORAGE_KEYS } from '@constants/common';

export const AUTH_KEY = 'authorized';

interface IAuthState {
	user: IUserKeycloak | null;
	accessPermission: Record<TKeyOfUserRoles, boolean>;
	userRoles: string[];
	organizationCategories: Array<keyof TOrganizationType>;
	organizationTimezone: string;
	activeOrganisation?: string;
	branding: IBrandingSetup | null;
	availableVendors: string[];
}

const initialState: IAuthState = {
	user: {} as IUserKeycloak,
	accessPermission: generateDefaultAccessPermission,
	userRoles: [],
	organizationCategories: [],
	organizationTimezone: '',
	branding: null,
	activeOrganisation: undefined,
	availableVendors: [],
};

const auth = createSlice({
	name: AUTH_KEY,
	initialState,
	reducers: {
		setActiveOrganisations: (state, action: PayloadAction<string | undefined>) => {
			state.activeOrganisation = action.payload;

			if (action.payload) {
				sessionStorage.setItem(STORAGE_KEYS.ORGANIZATION_ID, action.payload);
			} else {
				sessionStorage.removeItem(STORAGE_KEYS.ORGANIZATION_ID);
			}
		},
		setAvailableVendors: (state, action: PayloadAction<string[]>) => {
			state.availableVendors = action.payload;
		},
		setUser: (state, action: PayloadAction<IUserKeycloak>) => {
			state.user = { ...state.user, ...action.payload };

			const listOfRoles = action.payload?.roles;

			if (listOfRoles) {
				const permissions = Object.keys(EUserRoles).reduce<Record<keyof TUserRoles, boolean>>((acc, role) => {
					acc = { ...acc, [role]: listOfRoles.includes((EUserRoles as any)[(role)]) };

					return acc;
				}, {} as Record<keyof TUserRoles, boolean>);

				state.accessPermission = {
					...state.accessPermission,
					...permissions,
				};

				sessionStorage.setItem(
					STORAGE_KEYS.PLATFORM_OWNER_ADMINISTRATOR,
					String((permissions as any).platformOwnerAdministrator || false),
				);
			}
		},
		setUpdatedUserRoles: (state, action: PayloadAction<string[]>) => {
			if (state.user) {
				state.user.roles = action.payload;

				const permissions = Object.keys(EUserRoles).reduce<Record<keyof TUserRoles, boolean>>((acc, role) => {
					acc = { ...acc, [role]: action.payload.includes((EUserRoles as any)[(role)]) };

					return acc;
				}, {} as Record<keyof TUserRoles, boolean>);

				state.accessPermission = {
					...state.accessPermission,
					...permissions,
				};

				sessionStorage.setItem(
					STORAGE_KEYS.PLATFORM_OWNER_ADMINISTRATOR,
					String((permissions as any)?.platformOwnerAdministrator || false),
				);
			}
		},
		setUserId: (state, action: PayloadAction<string>) => {
			if (state.user) state.user.userId = action.payload;
		},
		setOrganizationCategories: (state, action: PayloadAction<Array<keyof TOrganizationType>>) => {
			state.organizationCategories = action.payload;
		},
		setOrganizationTimezone: (state, action: PayloadAction<string>) => {
			state.organizationTimezone = action.payload;
		},
		setAvatar: (state, action: PayloadAction<TDashboardAvatar>) => {
			if (state.user) {
				state.user.avatar = action.payload;
			}
		},
		setBranding: (state, action: PayloadAction<IBrandingSetup>) => {
			state.branding = action.payload;
		},
	},
});

export const {
	setUser,
	setOrganizationCategories,
	setOrganizationTimezone,
	setUpdatedUserRoles,
	setUserId,
	setAvatar,
	setBranding,
	setActiveOrganisations,
	setAvailableVendors,
} = auth.actions;

export default auth.reducer;
